import { useCallback, useEffect, useState } from "react";
import { Download, ShieldCheck, ShieldAlert, Search, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PAGE = 50;
const TONE = { denied: "bg-rose-100 text-rose-700", login_failed: "bg-rose-100 text-rose-700", lockout: "bg-rose-100 text-rose-700", delete: "bg-rose-50 text-rose-700",
               export: "bg-amber-100 text-amber-800", update: "bg-sky-100 text-sky-800", create: "bg-emerald-100 text-emerald-700", login: "bg-emerald-50 text-emerald-700" };

export function AuditLogTable() {
  const [f, setF] = useState({ q: "", action: "", resource_type: "", actor_id: "", from_date: "", to_date: "" });
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chain, setChain] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/compliance/audit", { params: { ...f, limit: PAGE, offset: page * PAGE } });
      setData(d);
    } catch { toast.error("Could not load the audit log"); }
    finally { setLoading(false); }
  }, [f, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/compliance/audit/verify").then((r) => setChain(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/compliance/audit/export.csv", { params: f, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "audit-log.csv" });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setPage(0); };
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const sel = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm";

  return (
    <div className="space-y-4" data-testid="audit-log">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={f.q} onChange={set("q")} placeholder="Search person or path" data-testid="audit-search" className={`${sel} pl-9 w-56`} />
        </label>
        <select value={f.action} onChange={set("action")} data-testid="audit-filter-action" className={sel}><option value="">All actions</option>{(data?.actions || []).map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}</select>
        <select value={f.resource_type} onChange={set("resource_type")} data-testid="audit-filter-resource" className={sel}><option value="">All records</option>{(data?.resource_types || []).map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}</select>
        <select value={f.actor_id} onChange={set("actor_id")} data-testid="audit-filter-actor" className={sel}><option value="">Everyone</option>{(data?.actors || []).map((a) => <option key={a.id} value={a.id}>{a.name} · {a.role}</option>)}</select>
        <input type="date" value={f.from_date} onChange={set("from_date")} data-testid="audit-from" className={sel} />
        <input type="date" value={f.to_date} onChange={set("to_date")} data-testid="audit-to" className={sel} />
        <div className="flex-1" />
        {chain && (
          <span data-testid="audit-chain-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${chain.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {chain.ok ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />} {chain.ok ? `Tamper-evident · ${chain.checked} entries verified` : `Chain broken at #${chain.first_broken_seq}`}
          </span>
        )}
        <button onClick={exportCsv} data-testid="audit-export-csv" className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> CSV</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
            <th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Who</th><th className="px-4 py-2.5">Action</th><th className="px-4 py-2.5">Record</th><th className="px-4 py-2.5">Subject</th><th className="px-4 py-2.5">IP</th><th className="px-4 py-2.5">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.items || []).map((e) => (
              <tr key={e.id} data-testid={`audit-row-${e.seq}`} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">{e.seq}</td>
                <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{new Date(e.ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="px-4 py-2.5"><div className="font-medium text-slate-900">{e.actor_name || <span className="text-slate-400">anonymous</span>}</div><div className="text-[11px] text-slate-500 capitalize">{(e.actor_role || e.actor_type).replace(/_/g, " ")}</div></td>
                <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${TONE[e.action] || "bg-slate-100 text-slate-700"}`}>{e.action.replace("_", " ")}</span></td>
                <td className="px-4 py-2.5 text-slate-700 capitalize">{e.resource_type.replace(/_/g, " ")}</td>
                <td className="px-4 py-2.5 text-slate-900">{e.subject || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{e.ip}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{e.status}</td>
              </tr>
            ))}
            {!loading && data && data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No audit events match these filters.</td></tr>}
            {loading && !data && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span data-testid="audit-total">{total} event{total === 1 ? "" : "s"}</span>
        <div className="inline-flex items-center gap-2">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="audit-prev" className="h-8 w-8 rounded-lg border border-slate-200 inline-flex items-center justify-center disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          <span>Page {page + 1} / {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} data-testid="audit-next" className="h-8 w-8 rounded-lg border border-slate-200 inline-flex items-center justify-center disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Every view, edit, export and sign-in involving health or personal records is recorded with a SHA-256 hash chained to the previous entry, so any alteration is detectable. Retained per your record-retention setting.</p>
    </div>
  );
}
